import { cx, useResizeObserver, useTranslations } from "@jugl-web/utils";
import { useSessionStorage } from "@jugl-web/utils/hooks/useStorage";
import { CRM_EMAIL_MESSAGE_EXPANDED_KEY_PREFIX } from "@jugl-web/utils/storage";
import { PlainButton } from "@jugl-web/ui-kit";
import React, { useCallback, useMemo, useState } from "react";
import { emailBodyToHtml } from "@web-src/modules/client-support/utils/emailBodyToHtml";
import { ReactComponent as ArrowDownIcon } from "./assets/arrow-down.svg";

// keep collapsed bodies short so the virtualized message list stays stable;
// taller bodies are clipped behind a Show more / Show less toggle
const MAX_COLLAPSED_HEIGHT = 300;

export const EmailMessageBody: React.FC<{
  messageId: string;
  text: string;
}> = ({ messageId, text }) => {
  const { t } = useTranslations();

  const html = useMemo(() => emailBodyToHtml(text), [text]);

  const [isExpanded, setIsExpanded] = useSessionStorage(
    `${CRM_EMAIL_MESSAGE_EXPANDED_KEY_PREFIX}${messageId}`,
    false
  );

  // measure the inner (never height-clipped) node so the result is stable
  // whether collapsed or expanded
  const [isOverflowing, setIsOverflowing] = useState(false);
  const handleResize = useCallback(
    (entry: ResizeObserverEntry) =>
      setIsOverflowing(entry.target.scrollHeight > MAX_COLLAPSED_HEIGHT),
    []
  );
  const { ref } = useResizeObserver<HTMLDivElement>({ onResize: handleResize });

  const isCollapsed = !isExpanded && isOverflowing;

  return (
    // when the toggle is shown, match the bottom padding to the gap above it
    <div className={cx("px-6", isOverflowing ? "pb-3.5" : "pb-5")}>
      <div
        className={cx(
          "relative",
          isCollapsed && "max-h-[300px] overflow-hidden"
        )}
      >
        <div
          ref={ref}
          className="font-secondary text-sm leading-[160%] wrap-break-word text-dark-800 [&_a]:text-primary [&_a]:underline [&_img]:max-w-full [&_img]:rounded-lg"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {isCollapsed && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[30px] bg-linear-to-t from-white to-transparent" />
        )}
      </div>
      {isOverflowing && (
        <>
          <div className="-mx-6 mt-3.5 h-px bg-dark-100" />
          <PlainButton
            onClick={() => setIsExpanded((value) => !value)}
            className="mt-3.5 flex items-center gap-1.5 font-secondary text-sm font-medium text-primary"
          >
            {isExpanded
              ? t({
                  id: "client-support-page.read-less",
                  defaultMessage: "Read Less",
                })
              : t({
                  id: "client-support-page.read-more",
                  defaultMessage: "Read More",
                })}
            <ArrowDownIcon
              className={cx(
                "shrink-0 transition-transform",
                isExpanded && "rotate-180"
              )}
            />
          </PlainButton>
        </>
      )}
    </div>
  );
};
